import React, { useEffect, useState } from "react"
import "./index.scss"

export const SEARCHING_MESSAGE = "Searching…"
export const LOADING_MESSAGE = "Loading…"

/**
 * A progress line for screen readers only.
 *
 * A live region has to be in the document before its content changes or
 * assistive technology does not announce it. So pages keep this rendered and
 * change its text, and the region always mounts empty and fills its text in
 * straight after -- which is also what lets a skeleton's own label be heard.
 * Keep it outside any `aria-busy` element: a busy region's changes may be held
 * back until it stops being busy, by which time this has emptied again.
 *
 * "Searching…" is for a new search term that has not been answered yet;
 * "Loading…" is for everything else -- a page change, a sort, a refresh.
 * `message` overrides both.
 */
export const LoadingStatus = ({ searching = false, loading = false, message = null }) => {
	let wanted = ""

	if (message) {
		wanted = message
	} else if (searching) {
		wanted = SEARCHING_MESSAGE
	} else if (loading) {
		wanted = LOADING_MESSAGE
	}

	const [text, setText] = useState("")

	useEffect(() => {
		setText(wanted)
	}, [wanted])

	return (
		<span className="LoadingStatus" role="status">
			{text}
		</span>
	)
}

/**
 * A plain <input> with a small spinner inside its right edge while `busy`.
 *
 * For the admin pickers that are raw inputs styled by their page. Fields that are
 * already semantic-ui `Input`s use that component's own `loading` prop instead,
 * which draws the same spinner. The spinner is decorative (`aria-hidden`); pair
 * this with a LoadingStatus for the announcement.
 */
export const SpinnerInput = ({ busy = false, className = "", ...inputProps }) => (
	<div className="SpinnerInput">
		<input {...inputProps} className={className ? `SpinnerInput-field ${className}` : "SpinnerInput-field"} />
		{busy && <span className="SpinnerInput-spinner" aria-hidden="true" />}
	</div>
)
